import {FC} from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from '@mui/material';

interface LimitRow {
    name: string;
    freeValue: string | number;
    subscribedValue: string | number;
    note?: string;
}

const UNLIMITED = '무제한';

function formatValue(v: number | string): string {
    if (typeof v === 'string') return v;
    if (v === 2147483647) return UNLIMITED; // Int.MAX_VALUE
    return v.toLocaleString();
}

// 단어장/슬롯 제한 — SubscriptionLimits.kt
// (단어장 개수/단어장당 단어 수/총 단어 수 제한은 2026-04-27 폐지됨)
const slotLimits: LimitRow[] = [
    {name: '그룹 생성', freeValue: '불가 (0)', subscribedValue: UNLIMITED},
    {name: '언어별 PERSONAL 단어 수', freeValue: 100, subscribedValue: 2147483647, note: 'head_lang별 개인 등록 단어 상한'},
];

// AI 주간 크레딧 — AiCreditService.kt (2026-04-20 일일 사용량 4종 → 주간 통합 크레딧)
const aiCreditLimits: LimitRow[] = [
    {name: '주간 AI 크레딧', freeValue: 100, subscribedValue: 3000, note: 'AI 단어/이미지/주제 생성·매직카드 공통 차감'},
];

const allSections = [
    {title: '단어장 / 슬롯 제한', rows: slotLimits},
    {title: 'AI 크레딧 (주간)', rows: aiCreditLimits},
];

const SubscriptionLimitsPage: FC = () => {
    return (
        <Box sx={{p: 3, maxWidth: 900}}>
            <Typography variant="h5" sx={{mb: 0.5}}>
                구독 제한 설정
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                FREE / SUBSCRIBED 사용자별 제한 상수입니다. 수정은 서버 코드에서 해야 합니다.
            </Typography>

            {allSections.map((section) => (
                <Card key={section.title} sx={{mb: 2}}>
                    <CardContent sx={{p: 0, '&:last-child': {pb: 0}}}>
                        <Typography variant="h6" sx={{px: 2, pt: 2, pb: 1}}>
                            {section.title}
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>항목</TableCell>
                                        <TableCell align="center">FREE</TableCell>
                                        <TableCell align="center">SUBSCRIBED</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {section.rows.map((row) => {
                                        const freeDisplay = formatValue(row.freeValue);
                                        const subDisplay = formatValue(row.subscribedValue);
                                        return (
                                            <TableRow key={row.name}>
                                                <TableCell>
                                                    {row.name}
                                                    {row.note && (
                                                        <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>
                                                            {row.note}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={freeDisplay}
                                                        size="small"
                                                        variant="outlined"
                                                        color={freeDisplay === '불가' || freeDisplay === '비활성화 (0)' || freeDisplay === '불가 (0)' ? 'error' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={subDisplay}
                                                        size="small"
                                                        color={subDisplay === UNLIMITED || subDisplay === '사용 가능' ? 'primary' : 'default'}
                                                        variant={subDisplay === UNLIMITED || subDisplay === '사용 가능' ? 'filled' : 'outlined'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            ))}

            <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>
                파일 위치: backend/.../subscription/limits/SubscriptionLimits.kt · backend/.../ai/credit/AiCreditService.kt
            </Typography>
        </Box>
    );
};

export default SubscriptionLimitsPage;
